import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSession } from '@/lib/auth';
import { commentsListQueryOptions } from '@/lib/comments';
import { useMobileSidebar } from '@/lib/mobile-sidebar';
import { ACTIVE_NAV, type NavItem, PLACEHOLDER_NAV, navItemsFor } from '@/lib/nav-items';
import { hasPermission } from '@/lib/permissions';
import { postsListQueryOptions } from '@/lib/posts';
import { settingsListQueryOptions } from '@/lib/settings';
import { useSidebarCollapsed } from '@/lib/sidebar-state';
import { usersListQueryOptions } from '@/lib/users';

const PLACEHOLDER = PLACEHOLDER_NAV;

export function Sidebar() {
  const [collapsed] = useSidebarCollapsed();
  const { open: mobileOpen, setOpen: setMobileOpen } = useMobileSidebar();
  const { user } = useSession();
  // Settings drive feature-flag gates on nav items (e.g. hide /comments
  // when comments.enabled=false). GET /api/admin/settings is auth-only,
  // no per-permission gate, so every logged-in user can read flags.
  // Fail-open inside navItemsFor when this is undefined.
  const settingsQuery = useQuery(settingsListQueryOptions());
  const settingsMap = useMemo(() => {
    if (!settingsQuery.data) return undefined;
    return new Map(settingsQuery.data.map((s) => [s.key, s.value]));
  }, [settingsQuery.data]);
  // Filter the nav by what THIS user can actually use, then drop /account
  // (lives in the header dropdown). Backend permission middleware still
  // enforces — this is purely a clean-UI win.
  const ACTIVE = navItemsFor(user, ACTIVE_NAV, settingsMap).filter(
    (item) => item.to !== '/account',
  );

  // Pending-review queue indicator on the "บทความข่าว" nav item. Only
  // publishers care (they're the ones who approve), and only when the
  // queue is non-empty — silence is the better default than "0". A 60s
  // stale time keeps the sidebar from re-fetching on every nav click;
  // the dashboard StatsRow already gives a more detailed live view.
  const canPublish = hasPermission(user, 'post.publish');
  const pendingQuery = useQuery({
    ...postsListQueryOptions({ status: ['pending_review'], perPage: 1 }),
    enabled: canPublish,
    staleTime: 60_000,
  });
  const pendingCount = pendingQuery.data?.pagination.total ?? 0;

  // Same shape for /comments — moderators see the queue depth without
  // having to enter the page. Silent when zero or when moderator lacks
  // the permission. Same 60s stale time.
  const canModerateComments = hasPermission(user, 'comment.moderate');
  const pendingCommentsQuery = useQuery({
    ...commentsListQueryOptions({ status: 'pending', perPage: 1 }),
    enabled: canModerateComments,
    staleTime: 60_000,
  });
  const pendingCommentsCount = pendingCommentsQuery.data?.pagination.total ?? 0;

  // Same shape for /users — admins see "how many in required roles
  // still haven't enrolled in 2FA?" without having to enter the page +
  // click the filter pill. Pairs with the Required2faBanner that nags
  // the affected users. Silent when zero (= nothing to chase) or when
  // the viewer lacks user.update. Backend's required-missing filter
  // already fails open on missing/corrupted setting → count of 0 → no
  // badge, so an unconfigured install stays quiet.
  const canManageUsers = hasPermission(user, 'user.update');
  const required2faMissingQuery = useQuery({
    ...usersListQueryOptions({ twoFactor: 'required-missing', perPage: 1 }),
    enabled: canManageUsers,
    staleTime: 60_000,
  });
  const required2faMissingCount = required2faMissingQuery.data?.pagination.total ?? 0;

  // Desktop: width toggle expanded/collapsed. Mobile (sm: and below): drawer
  // is always 240px wide and slides off-screen via translate. Desktop width
  // must be `sm:` prefixed so it only applies at sm+ — otherwise the mobile
  // `w-[240px]` would override it at every breakpoint.
  const desktopWidth = collapsed ? 'sm:w-14' : 'sm:w-[220px]';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 sm:hidden ${
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <aside
        className={[
          // Shared
          'flex flex-col bg-[#393939]',
          // Mobile base: fixed drawer 240px, slides off when closed.
          'fixed inset-y-0 left-0 z-50 w-[240px] transform transition-transform duration-200',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop overrides: static + sticky + width toggle (must be sm: prefix
          // so it beats the base w-[240px]).
          'sm:sticky sm:left-auto sm:top-0 sm:z-auto sm:h-screen sm:shrink-0 sm:translate-x-0 sm:transition-[width] sm:duration-[220ms] sm:ease-[cubic-bezier(.4,0,.2,1)]',
          desktopWidth,
        ].join(' ')}
      >
        {/* Brand block + mobile close button */}
        <div
          className={`flex h-14 items-center border-b border-white/[0.06] ${collapsed ? 'sm:justify-center sm:px-2' : 'gap-2.5 px-4'}`}
        >
          <img
            src="/news1-logo.png"
            alt="News1Live"
            width={34}
            height={34}
            className="shrink-0 rounded-md"
          />
          {/* Lockup hidden when collapsed on desktop; always visible on mobile drawer */}
          <div className={`min-w-0 leading-tight sm:${collapsed ? 'hidden' : 'block'}`}>
            <div className="text-[13px] font-semibold text-white">News1Live</div>
            <div className="text-[10px] text-white/60">CMS Backend</div>
          </div>
          {/* Mobile close button — desktop only ever uses header's collapse toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="ปิดเมนู"
            className="ml-auto rounded p-1 text-white/70 hover:bg-white/[0.04] hover:text-white sm:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden px-2 py-3">
          {ACTIVE.map((item) => {
            // Submenu group — renders as expandable header (when sidebar is
            // expanded) or just flattens children inline (when collapsed,
            // since icons-only would make the group label invisible anyway).
            if (item.children && item.children.length > 0) {
              if (collapsed) {
                return (
                  <div key={item.label} className="contents">
                    {item.children.map((child) =>
                      renderLeaf(child, {
                        collapsed,
                        canPublish,
                        pendingCount,
                        canModerateComments,
                        pendingCommentsCount,
                        canManageUsers,
                        required2faMissingCount,
                        onNavigate: () => setMobileOpen(false),
                      }),
                    )}
                  </div>
                );
              }
              return (
                <NavGroup
                  key={item.label}
                  item={item}
                  renderLeaf={(child) =>
                    renderLeaf(child, {
                      collapsed,
                      canPublish,
                      pendingCount,
                      canModerateComments,
                      pendingCommentsCount,
                      canManageUsers,
                      required2faMissingCount,
                      onNavigate: () => setMobileOpen(false),
                      indent: true,
                    })
                  }
                />
              );
            }
            return renderLeaf(item, {
              collapsed,
              canPublish,
              pendingCount,
              canModerateComments,
              pendingCommentsCount,
              canManageUsers,
              required2faMissingCount,
              onNavigate: () => setMobileOpen(false),
            });
          })}

          <div className="my-2 border-t border-white/[0.06]" />

          {PLACEHOLDER.map((item) => (
            <span
              key={item.label}
              title={collapsed ? `${item.label} (Soon)` : undefined}
              className={
                collapsed
                  ? 'flex cursor-not-allowed items-center gap-3 rounded-md py-2.5 pl-3 pr-3 text-[13px] text-white/30 sm:justify-center sm:gap-0 sm:px-2'
                  : 'flex cursor-not-allowed items-center gap-3 rounded-md py-2.5 pl-3 pr-3 text-[13px] text-white/30'
              }
            >
              <item.icon className="h-[18px] w-[18px] shrink-0" />
              <span className={collapsed ? 'flex-1 truncate sm:hidden' : 'flex-1 truncate'}>
                {item.label}
              </span>
              <span
                className={
                  collapsed
                    ? 'rounded bg-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white/60 sm:hidden'
                    : 'rounded bg-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white/60'
                }
              >
                Soon
              </span>
            </span>
          ))}
        </nav>

        {/* Footer hidden on collapsed desktop, always visible on mobile drawer */}
        <div
          className={`border-t border-white/[0.06] px-4 py-2 text-[10px] text-white/40 ${collapsed ? 'sm:hidden' : ''}`}
        >
          v0.2.0 · Pure Cloudflare
        </div>
      </aside>
    </>
  );
}

/* ---------- helpers ---------- */

interface RenderLeafOptions {
  collapsed: boolean;
  canPublish: boolean;
  pendingCount: number;
  canModerateComments: boolean;
  pendingCommentsCount: number;
  canManageUsers: boolean;
  required2faMissingCount: number;
  onNavigate: () => void;
  /** Slight left-margin to visually nest under a parent group header. */
  indent?: boolean;
}

function renderLeaf(item: NavItem, opts: RenderLeafOptions) {
  if (!item.to) return null;
  const {
    collapsed,
    canPublish,
    pendingCount,
    canModerateComments,
    pendingCommentsCount,
    canManageUsers,
    required2faMissingCount,
    onNavigate,
    indent,
  } = opts;
  const postsBadge = item.to === '/posts' && canPublish && pendingCount > 0;
  const commentsBadge = item.to === '/comments' && canModerateComments && pendingCommentsCount > 0;
  const usersBadge = item.to === '/users' && canManageUsers && required2faMissingCount > 0;
  const showPendingBadge = postsBadge || commentsBadge || usersBadge;
  const badgeCount = postsBadge
    ? pendingCount
    : commentsBadge
      ? pendingCommentsCount
      : required2faMissingCount;
  // /users is a security gap, not a workflow queue — different
  // tooltip wording so the reader doesn't conflate it with the
  // posts/comments "pending approval" semantics.
  const badgeAriaLabel = usersBadge
    ? `${badgeCount} ผู้ใช้ค้างเปิด 2FA`
    : `${badgeCount} รออนุมัติ`;
  const collapsedTitle = usersBadge
    ? `${item.label} (ค้างเปิด 2FA ${badgeCount})`
    : `${item.label} (รออนุมัติ ${badgeCount})`;
  const baseClass = collapsed
    ? 'relative flex items-center gap-3 rounded-md py-2.5 pl-3 pr-3 text-[13px] text-white/70 transition-colors hover:bg-white/[0.04] hover:text-white sm:justify-center sm:gap-0 sm:px-2'
    : 'relative flex items-center gap-3 rounded-md py-2.5 pl-3 pr-3 text-[13px] text-white/70 transition-colors hover:bg-white/[0.04] hover:text-white';
  // Indent only when expanded — collapsed icons are already centered.
  const indentClass = indent && !collapsed ? ' pl-6' : '';
  return (
    <Link
      key={item.to}
      to={item.to}
      onClick={onNavigate}
      title={collapsed ? (showPendingBadge ? collapsedTitle : item.label) : undefined}
      className={baseClass + indentClass}
      activeProps={{
        className: collapsed
          ? 'bg-[rgba(220,38,38,0.16)] text-[#F87171] sm:before:hidden before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-r before:bg-[#F87171]'
          : 'bg-[rgba(220,38,38,0.16)] text-[#F87171] before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-r before:bg-[#F87171]',
      }}
      activeOptions={{ exact: item.to === '/' }}
    >
      <item.icon className="h-[18px] w-[18px] shrink-0" />
      <span className={collapsed ? 'truncate sm:hidden' : 'flex-1 truncate'}>{item.label}</span>
      {showPendingBadge && (
        <span
          className={
            collapsed
              ? 'absolute right-1.5 top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white sm:right-1 sm:top-1 sm:h-2 sm:min-w-0 sm:px-0'
              : 'inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white'
          }
          aria-label={badgeAriaLabel}
        >
          {collapsed ? <span className="sm:hidden">{badgeCount}</span> : badgeCount}
        </span>
      )}
    </Link>
  );
}

/** Expandable submenu group — header (icon + label + chevron) + indented
 *  children below. Default open since pop-driven super_admin will be the
 *  only one seeing it, and the 3 children below are commonly used together. */
function NavGroup({
  item,
  renderLeaf: renderChildLeaf,
}: {
  item: NavItem;
  renderLeaf: (child: NavItem) => React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-md py-2.5 pl-3 pr-3 text-[13px] text-white/70 transition-colors hover:bg-white/[0.04] hover:text-white"
      >
        <item.icon className="h-[18px] w-[18px] shrink-0" />
        <span className="flex-1 truncate text-left">{item.label}</span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-white/40" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/40" />
        )}
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">{item.children?.map((c) => renderChildLeaf(c))}</div>
      )}
    </div>
  );
}
